import {
    AssessmentSchedule,
    DayOfWeek,
    MonthlyRecurrence,
    MonthlyScheduleType
} from "../../API";
import {
    Day,
    addDays,
    addMonths,
    addWeeks,
    differenceInDays,
    differenceInMonths,
    differenceInWeeks,
    endOfWeek,
    getDay,
    isAfter,
    isBefore,
    isSameDay,
    lastDayOfMonth,
    startOfMonth,
    startOfWeek
} from 'date-fns';

export class ScheduleChecker {
    private schedule: AssessmentSchedule;

    constructor(schedule: AssessmentSchedule) {
        this.schedule = schedule;
    }

    public getSchedule(): AssessmentSchedule {
        return this.schedule;
    }

    public isDateInSchedule(date: Date): boolean {
        if (!this.schedule.startDate || !this.schedule.recurrence) {
            return false;
        }
        const startDate = new Date(this.schedule.startDate);
        if (isBefore(date, startDate)) {
            return false;
        }
        const endDate = this.schedule.endDate ? new Date(this.schedule.endDate) : null;
        if (endDate && isAfter(date, endDate)) {
            return false;
        }

        switch (this.schedule.recurrence.type) {
            case "DAILY":
                return this.isInDailyRecurrence(date, startDate);
            case "WEEKLY":
                return this.isInWeeklyRecurrence(date, startDate);
            case "MONTHLY":
                return this.isInMonthlyRecurrence(date, startDate);
            default:
                return false;
        }
    }

    public getFirstScheduledDate(): Date | undefined {
        if (!this.schedule.startDate) {
            return undefined;
        }
        const startDate = new Date(this.schedule.startDate);
        switch (this.schedule.recurrence?.type) {
            case "DAILY":
                return startDate;
            case "WEEKLY":
                return this.getFirstWeeklyScheduleDate(startDate, this.schedule.recurrence?.weeklyRecurrence?.daysOfWeek as Array<DayOfWeek>);
            case "MONTHLY":
                switch (this.schedule.recurrence?.monthlyRecurrence?.type) {
                    case MonthlyScheduleType.DAY_OF_MONTH:
                        return this.getFirstDayOfMonthScheduleDate(startDate, this.schedule.recurrence.monthlyRecurrence);
                    case MonthlyScheduleType.NTH_WEEK_DAY:
                        return this.getFirstNthWeekDayScheduleDate(startDate, this.schedule.recurrence.monthlyRecurrence);
                    default:
                        return undefined;
                }
            default:
                return undefined;
        }
    }

    private isInDailyRecurrence(date: Date, startDate: Date): boolean {
        if (!this.schedule.recurrence?.dailyRecurrence ||
            this.schedule.recurrence.dailyRecurrence.interval == undefined
        ) {
            return false;
        }
        const { dailyRecurrence: { interval } } = this.schedule.recurrence;
        return differenceInDays(date, startDate) % interval === 0;
    }

    private isInWeeklyRecurrence(date: Date, startDate: Date): boolean {
        if (!this.schedule.recurrence?.weeklyRecurrence ||
            !this.schedule.recurrence.weeklyRecurrence.daysOfWeek ||
            this.schedule.recurrence.weeklyRecurrence.daysOfWeek.length === 0 ||
            this.schedule.recurrence.weeklyRecurrence.interval == undefined
        ) {
            return false;
        }
        const { weeklyRecurrence: { daysOfWeek, interval } } = this.schedule.recurrence;

        // Find the first occurrence based on daysOfWeek
        const firstScheduleDate = this.getFirstWeeklyScheduleDate(startDate, daysOfWeek as Array<DayOfWeek>);
        return differenceInWeeks(startOfWeek(date), startOfWeek(firstScheduleDate)) % interval === 0 && daysOfWeek.includes(this.getDayOfWeek(date));
    }

    private isInMonthlyRecurrence(date: Date, startDate: Date): boolean {
        if (!this.schedule.recurrence?.monthlyRecurrence ||
            this.schedule.recurrence.monthlyRecurrence.interval == undefined ||
            !this.schedule.recurrence.monthlyRecurrence.type
        ) {
            return false;
        }
        const { monthlyRecurrence: { interval } } = this.schedule.recurrence;
        let firstScheduleDate: Date;
        switch (this.schedule.recurrence.monthlyRecurrence.type) {
            case MonthlyScheduleType.DAY_OF_MONTH:
                if (this.schedule.recurrence.monthlyRecurrence.dayOfMonth == undefined) {
                    return false;
                }
                const { monthlyRecurrence: { dayOfMonth } } = this.schedule.recurrence;
                firstScheduleDate = this.getFirstDayOfMonthScheduleDate(startDate, this.schedule.recurrence.monthlyRecurrence);
                if (differenceInMonths(date, firstScheduleDate) % interval !== 0) {
                    return false;
                }
                const dayOfMonthNumber = parseInt(dayOfMonth);
                if (isNaN(dayOfMonthNumber) && dayOfMonth !== "L") {
                    return false;
                }
                if (dayOfMonth === "L" || dayOfMonthNumber >= lastDayOfMonth(date).getDate()) {
                    return isSameDay(date, lastDayOfMonth(date));
                }
                return date.getDate() === dayOfMonthNumber;
            case MonthlyScheduleType.NTH_WEEK_DAY:
                if (!this.schedule.recurrence.monthlyRecurrence.nthWeekDay ||
                    this.schedule.recurrence.monthlyRecurrence.nthWeekDay.day == undefined ||
                    this.schedule.recurrence.monthlyRecurrence.nthWeekDay.weekNumber == undefined
                ) {
                    return false;
                }
                firstScheduleDate = this.getFirstNthWeekDayScheduleDate(startDate, this.schedule.recurrence.monthlyRecurrence);
                if ((date.getMonth() - firstScheduleDate.getMonth()) % interval !== 0) {
                    return false;
                }
                const isDayOfWeekMatch = this.getDayOfWeek(date) === this.schedule.recurrence.monthlyRecurrence.nthWeekDay.day;
                if (!isDayOfWeekMatch) {
                    return false;
                }
                const weekNumber = parseInt(this.schedule.recurrence.monthlyRecurrence.nthWeekDay.weekNumber);
                if (isNaN(weekNumber)) {
                    if (this.schedule.recurrence.monthlyRecurrence.nthWeekDay.weekNumber !== "L") {
                        return false;
                    }
                    const targetDate = startOfWeek(lastDayOfMonth(date), { weekStartsOn: this.dayOfWeekToDay(this.schedule.recurrence.monthlyRecurrence.nthWeekDay.day) });
                    return isSameDay(date, targetDate);
                }
                const dayOfWeekInDay: Day = this.dayOfWeekToDay(this.schedule.recurrence.monthlyRecurrence.nthWeekDay.day);
                // Finding the first MONDAY/TUESDAY/... of the month
                const firstDayOfWeekInMonth = endOfWeek(startOfMonth(date), { weekStartsOn: (dayOfWeekInDay + 1) % 7 as Day });
                const targetDate = addWeeks(firstDayOfWeekInMonth, weekNumber - 1);
                return isSameDay(date, targetDate);
            default:
                return false;
        }
    }

    private getFirstWeeklyScheduleDate(startDate: Date, daysOfWeek: DayOfWeek[]): Date {
        let firstScheduleDate = startDate;
        for (let i = 0; i < 7; i++) {
            if (daysOfWeek.includes(this.getDayOfWeek(firstScheduleDate))) {
                return firstScheduleDate;
            }
            firstScheduleDate = addDays(firstScheduleDate, 1); // Find the first valid day in daysOfWeek
        }
        return firstScheduleDate;
    }

    private getFirstDayOfMonthScheduleDate(startDate: Date, monthlyRecurrence: MonthlyRecurrence): Date {
        const { dayOfMonth } = monthlyRecurrence;
        if (!dayOfMonth) {
            return startDate;
        }
        const dayOfMonthNumber = parseInt(dayOfMonth);
        if (isNaN(dayOfMonthNumber)) {
            if (dayOfMonth === "L") {
                return lastDayOfMonth(startDate);
            }
            return startDate;
        }
        if (dayOfMonthNumber >= lastDayOfMonth(startDate).getDate()) {
            return lastDayOfMonth(startDate);
        }
        const sameMonthDate = new Date(startDate.getFullYear(), startDate.getMonth(), dayOfMonthNumber);
        if (isAfter(sameMonthDate, startDate) || isSameDay(sameMonthDate, startDate)) {
            return sameMonthDate;
        }
        return addMonths(sameMonthDate, 1);
    }

    private getFirstNthWeekDayScheduleDate(startDate: Date, monthlyRecurrence: MonthlyRecurrence): Date {
        const { nthWeekDay } = monthlyRecurrence;
        if (!nthWeekDay || nthWeekDay.day == undefined || nthWeekDay.weekNumber == undefined) {
            return startDate;
        }
        const day: DayOfWeek = nthWeekDay.day;
        const weekNumber = parseInt(nthWeekDay.weekNumber);
        if (isNaN(weekNumber)) {
            if (nthWeekDay.weekNumber !== "L") {
                throw new Error("Invalid week number");
            }
            const currentMonthTargetDate = startOfWeek(lastDayOfMonth(startDate), { weekStartsOn: this.dayOfWeekToDay(day) });
            if (isAfter(currentMonthTargetDate, startDate) || isSameDay(currentMonthTargetDate, startDate)) {
                return currentMonthTargetDate;
            }
            const nextMonth = addMonths(startDate, 1);
            return startOfWeek(lastDayOfMonth(nextMonth), { weekStartsOn: this.dayOfWeekToDay(day) });
        }
        // Finding the first MONDAY/TUESDAY/... of the month
        const firstDayOfWeekInCurrentMonth = endOfWeek(startOfMonth(startDate), { weekStartsOn: (this.dayOfWeekToDay(day) + 1) % 7 as Day });
        const targetDateInCurrentMonth = addWeeks(firstDayOfWeekInCurrentMonth, weekNumber - 1);
        if (isAfter(targetDateInCurrentMonth, startDate) || isSameDay(targetDateInCurrentMonth, startDate)) {
            return targetDateInCurrentMonth;
        }
        const nextMonth = addMonths(startDate, 1);
        const firstDayOfWeekInNextMonth = endOfWeek(startOfMonth(nextMonth), { weekStartsOn: this.dayOfWeekToDay(day) });
        return addWeeks(firstDayOfWeekInNextMonth, weekNumber - 1);
    }

    private getDayOfWeek(date: Date): DayOfWeek {
        const daysOfWeek: DayOfWeek[] = Object.values(DayOfWeek);
        return daysOfWeek[getDay(date)];
    }

    private dayOfWeekToDay(dayOfWeek: DayOfWeek): Day {
        switch (dayOfWeek) {
            case DayOfWeek.SUNDAY:
                return 0;
            case DayOfWeek.MONDAY:
                return 1;
            case DayOfWeek.TUESDAY:
                return 2;
            case DayOfWeek.WEDNESDAY:
                return 3;
            case DayOfWeek.THURSDAY:
                return 4;
            case DayOfWeek.FRIDAY:
                return 5;
            case DayOfWeek.SATURDAY:
                return 6;
        }
    }
}